import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import { getToken } from "@/utils/auth";
import { spmPv } from "@/utils/spm";
import { getDashboardMenus, getUserDefaultSpace } from "@/api/space";

const whiteList = ["/login", "/register"];
const whiteListPrefixes = ["/book/", "/s/"];

function isWhiteListed(path) {
  if (whiteList.indexOf(path) !== -1) return true;
  return whiteListPrefixes.some((prefix) => path.startsWith(prefix));
}

// code → 路由配置映射表（前端控制组件路径，后端控制菜单可见性）
const childRouteMap = {
  DashboardPanel: {
    path: "panel",
    component: () => import("@/views/dashboard/panel/index.vue"),
  },
  DashboardCollect: {
    path: "collect",
    component: () => import("@/views/dashboard/collect/index.vue"),
  },
  DashboardBook: {
    path: "book",
    component: () => import("@/views/dashboard/book/index.vue"),
  },
  DashboardProfile: {
    path: "profile",
    component: () => import("@/views/dashboard/profile/index.vue"),
  },
  DashboardSettings: {
    path: "settings",
    component: () => import("@/views/dashboard/settings/index.vue"),
  },
  DashboardRecyclingBin: {
    path: "recycling-bin",
    component: () => import("@/views/dashboard/recycling-bin/index.vue"),
  },
  DashboardQuestion: {
    path: "question",
    component: () => import("@/views/dashboard/question/index.vue"),
  },
  DashboardTool: {
    path: "tool",
    component: () => import("@/views/dashboard/tool/index.vue"),
    children: {
      ToolMdConversion: {
        path: "md-conversion",
        component: () => import("@/views/dashboard/tool/md-conversion/index.vue"),
      },
    },
  },
};

function buildRouteConfig(menuItem) {
  const routeDef = childRouteMap[menuItem.code];
  if (!routeDef) return null;

  const route = {
    path: routeDef.path,
    name: menuItem.code,
    component: routeDef.component,
    meta: { title: menuItem.name },
  };

  if (routeDef.children) {
    route.children = Object.entries(routeDef.children).map(([code, child]) => ({
      path: child.path,
      name: code,
      component: child.component,
      meta: { title: menuItem.name },
    }));
  }

  return route;
}

async function addDynamicRoutes() {
  const dashboardRoute = router.options.routes.find((r) => r.name === "Dashboard");
  if (dashboardRoute.children && dashboardRoute.children.length > 0) return;

  try {
    const res = await getDashboardMenus();
    if (res.code === 200 && res.data) {
      const dynamicChildren = res.data
        .map((menu) => buildRouteConfig(menu))
        .filter(Boolean);
      dashboardRoute.children = dynamicChildren;
      dynamicChildren.forEach((child) => {
        router.addRoute("Dashboard", child);
      });
    }
  } catch (error) {
    console.error("加载动态路由失败:", error);
  }
}

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/login/index.vue"),
    meta: { title: "登录" },
  },
  {
    path: "/dashboard/:spaceId",
    name: "Dashboard",
    component: () => import("@/views/dashboard/index.vue"),
    children: [],
  },
  {
    path: "/book/:bookId",
    name: "BookDetail",
    component: () => import("@/views/book-detail/index.vue"),
    meta: { title: "知识库详情" },
  },
  {
    path: "/s/:docId",
    name: "DocDetailShare",
    component: () => import("@/views/doc-detail-share/index.vue"),
    meta: { title: "文档分享" },
  },
  {
    path: "/403",
    name: "Forbidden",
    component: () => import("@/views/403/index.vue"),
    meta: { title: "无权限" },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/404/index.vue"),
    meta: { title: "" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.meta.title) {
    document.title = to.meta.title + " | Omni";
  }

  const hasToken = getToken();

  if (hasToken) {
    if (to.path === "/login") {
      try {
        const res = await getUserDefaultSpace();
        if (res.code === 200 && res.data && res.data.spaceId) {
          next({ path: `/dashboard/${res.data.spaceId}` });
        } else {
          next();
        }
      } catch {
        next();
      }
    } else {
      // 动态路由未加载时，先加载再重试
      if (!store.getters.dynamicRoutesLoaded) {
        try {
          const hasUserInfo = store.getters.userInfo && store.getters.userInfo.userId;
          if (!hasUserInfo) {
            await store.dispatch("getUserInfo");
          }
          await addDynamicRoutes();
          store.commit("SET_DYNAMIC_ROUTES_LOADED", true);
          next({ path: to.fullPath, replace: true });
        } catch (error) {
          await store.dispatch("logout");
          next(`/login?redirect=${to.path}`);
        }
      } else {
        next();
      }
    }
  } else {
    if (isWhiteListed(to.path)) {
      next();
    } else {
      next(`/login?redirect=${to.path}`);
    }
  }
});

router.afterEach((to) => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  const pageMap = {
    Login: "login",
    DashboardPanel: "dashboard.panel",
    DashboardCollect: "dashboard.collect",
    DashboardBook: "dashboard.book",
    DashboardProfile: "dashboard.profile",
    DashboardSettings: "dashboard.settings",
    BookDetail: "book-detail",
    Test: "test",
  };
  const page = pageMap[to.name] || to.name || to.path;
  spmPv(page);
});

export default router;
